import React, { useState, useEffect } from 'react';
import { getData, postData, putData, deleteData, API_BASE_URL, API_TOKEN } from '../helpers/ApiSettings';
import ProductSalesTable from '../components/ProductSales/ProductSalesTable';
import AddProductSaleModal from '../components/ProductSales/AddProductSaleModal';
import EditProductSaleModal from '../components/ProductSales/EditProductSaleModal';

const ProductSales = () => {
  const [productSales, setProductSales] = useState([]);
  const [saleCategories, setSaleCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingProductSale, setEditingProductSale] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(8); // Items per page

  const [newProductSale, setNewProductSale] = useState({
    title: '',
    ean_code: '',
    description: '',
    unit_price: '',
    bulk_quantity: 2,
    bulk_price: null,
    stock_quantity: 0,
    sale_category: null,
    brand: '',
    age_range: '',
    weight: null,
    is_active: true,
    featured: false,
    is_private: false,
    private_price: null,
    meta_title: '',
    meta_description: ''
  });

  const fetchProductSales = async (page = currentPage) => {
    try {
      setLoading(true);
      // Add pagination parameters to the API call
      const response = await getData(`/product-sales?populate=*&pagination[page]=${page}&pagination[pageSize]=${pageSize}&sort[0]=id:desc`);
      setProductSales(response.data.data);
      // Calculate total pages from meta data
      const total = response.data.meta.pagination.total;
      setTotalPages(Math.ceil(total / pageSize));
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des produits en vente');
      console.error('Error fetching product sales:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSaleCategories = async () => {
    try {
      const response = await getData('/sale-categories?sort[0]=order:asc&sort[1]=name:asc');
      setSaleCategories(response.data.data);
    } catch (err) {
      console.error('Error fetching sale categories:', err);
    }
  };

  useEffect(() => {
    fetchProductSales();
  }, [currentPage]); // Re-fetch when page changes

  useEffect(() => {
    fetchSaleCategories();
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0); // Scroll to top when page changes
  };

  // Pagination component
  const Pagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <li key="prev" className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
        <button
          className="page-link"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &laquo;
        </button>
      </li>
    );

    // First page if not visible
    if (startPage > 1) {
      pages.push(
        <li key={1} className="page-item">
          <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
        </li>
      );
      if (startPage > 2) {
        pages.push(<li key="ellipsis1" className="page-item disabled"><span className="page-link">...</span></li>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
          <button className="page-link" onClick={() => handlePageChange(i)}>{i}</button>
        </li>
      );
    }

    // Last page if not visible
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<li key="ellipsis2" className="page-item disabled"><span className="page-link">...</span></li>);
      }
      pages.push(
        <li key={totalPages} className="page-item">
          <button className="page-link" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
        </li>
      );
    }

    // Next button
    pages.push(
      <li key="next" className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
        <button
          className="page-link"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          &raquo;
        </button>
      </li>
    );

    return (
      <nav aria-label="Product sales navigation" className="mt-4">
        <ul className="pagination justify-content-center">
          {pages}
        </ul>
      </nav>
    );
  };

  const handleAddProductSale = async (e, productSaleData) => {
    e.preventDefault();
    try {
      const { files, ...productSaleDetails } = productSaleData;
      const response = await postData('/product-sales', {
        data: productSaleDetails
      });

      const newProductSaleId = response.data.data.id;

      if (files && files.length > 0) {
        await handleImageUpload(newProductSaleId, files);
      }

      await fetchProductSales(currentPage);
      setNewProductSale({
        title: '',
        ean_code: '',
        description: '',
        unit_price: '',
        bulk_quantity: 2,
        bulk_price: null,
        stock_quantity: 0,
        sale_category: null,
        brand: '',
        age_range: '',
        weight: null,
        is_active: true,
        featured: false,
        is_private: false,
        private_price: null,
        meta_title: '',
        meta_description: ''
      });

      window.$('#addProductSaleModal').modal('hide');
      document.querySelector('.modal-backdrop')?.remove();
      alert('Produit en vente ajouté avec succès');
    } catch (err) {
      alert('Erreur lors de l\'ajout du produit en vente');
      console.error('Error adding product sale:', err);
    }
  };

  const handleUpdateProductSale = async (e) => {
    e.preventDefault();
    try {
      // First, get the updated product data to get the latest images after upload/delete operations
      const response = await getData(`/product-sales/${editingProductSale.documentId}?populate=*`);
      console.log('API Response:', response); // Debug log
      
      const currentProductData = response.data.data;
      console.log('Current Product Data:', currentProductData); // Debug log

      // Handle different possible response structures
      let currentImages = [];
      if (currentProductData.attributes?.images?.data) {
        // Strapi v4 format
        currentImages = currentProductData.attributes.images.data.map(img => img.id);
      } else if (currentProductData.images?.data) {
        // Alternative format
        currentImages = currentProductData.images.data.map(img => img.id);
      } else if (Array.isArray(currentProductData.images)) {
        // Direct array format
        currentImages = currentProductData.images.map(img => img.id || img);
      } else if (Array.isArray(currentProductData.attributes?.images)) {
        // Direct array in attributes
        currentImages = currentProductData.attributes.images.map(img => img.id || img);
      }

      console.log('Extracted image IDs:', currentImages); // Debug log

      const updateData = {
        title: editingProductSale.title,
        ean_code: editingProductSale.ean_code,
        description: editingProductSale.description,
        unit_price: editingProductSale.unit_price,
        bulk_quantity: editingProductSale.bulk_quantity,
        bulk_price: editingProductSale.bulk_price,
        stock_quantity: editingProductSale.stock_quantity,
        sale_category: editingProductSale.sale_category?.id || editingProductSale.sale_category || null,
        brand: editingProductSale.brand,
        age_range: editingProductSale.age_range,
        weight: editingProductSale.weight,
        is_active: editingProductSale.is_active,
        featured: editingProductSale.featured,
        is_private: editingProductSale.is_private ?? false,
        private_price: editingProductSale.private_price ?? null,
        meta_title: editingProductSale.meta_title,
        meta_description: editingProductSale.meta_description,
        // Use the current images from the database (which now include new uploads and exclude deleted ones)
        images: currentImages
      };

      console.log('Update data:', updateData); // Debug log

      await putData(`/product-sales/${editingProductSale.documentId}`, {
        data: updateData
      });

      // Refresh the product list after successful update
      await fetchProductSales(currentPage);

      // Close modal and cleanup
      window.$('#editProductSaleModal').modal('hide');
      document.querySelector('.modal-backdrop')?.remove();
      setEditingProductSale(null);
      alert('Produit en vente mis à jour avec succès');
    } catch (err) {
      console.error('Error updating product sale:', err.response?.data || err);
      alert('Erreur lors de la mise à jour du produit en vente: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleDeleteProductSale = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit en vente ?')) {
      try {
        await deleteData(`/product-sales/${id}`);
        await fetchProductSales(currentPage);
        alert('Produit en vente supprimé avec succès');
      } catch (err) {
        alert('Erreur lors de la suppression du produit en vente');
        console.error('Error deleting product sale:', err);
      }
    }
  };

  const handleEditClick = (productSale) => {
    const productSaleCopy = JSON.parse(JSON.stringify(productSale));
    setEditingProductSale(productSaleCopy);
    window.$('#editProductSaleModal').modal('show');
  };

  const handleImageUpload = async (productSaleId, files) => {
    try {
      const formData = new FormData();
      formData.append('ref', 'api::product-sale.product-sale');
      formData.append('refId', productSaleId);
      formData.append('field', 'images');

      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${API_TOKEN}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Upload failed');
      }

      // await fetchProductSales(currentPage);
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Erreur lors du téléchargement des images');
      throw error;
    }
  };

  const handleImageDelete = async (productSaleId, imageId) => {
    try {
      await deleteData(`/upload/files/${imageId}`);

      // Don't need to update local state here as the modal handles it
      // The database deletion is what matters for the final save
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Erreur lors de la suppression de l\'image');
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Produits en Vente</h2>
        <button
          className="btn btn-primary"
          onClick={() => window.$('#addProductSaleModal').modal('show')}
        >
          Ajouter un produit en vente
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          {productSales.length === 0 ? (
            <p className="text-center text-muted my-5">Aucun produit en vente pour le moment</p>
          ) : (
            <>
              <ProductSalesTable
                productSales={productSales}
                onEdit={handleEditClick}
                onDelete={handleDeleteProductSale}
              />
              <Pagination />
            </>
          )}
        </div>
      </div>

      <AddProductSaleModal
        newProductSale={newProductSale}
        setNewProductSale={setNewProductSale}
        onSubmit={handleAddProductSale}
        onImageUpload={handleImageUpload}
        saleCategories={saleCategories}
      />

      <EditProductSaleModal
        editingProductSale={editingProductSale}
        setEditingProductSale={setEditingProductSale}
        onSubmit={handleUpdateProductSale}
        onImageUpload={handleImageUpload}
        onImageDelete={handleImageDelete}
        saleCategories={saleCategories}
      />
    </div>
  );
};

export default ProductSales;